import { Alert, Badge, Button, cardClass, fieldClass, selectClass, Table, textareaClass } from '@ledgerloom/ui';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, propose } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { badgeTone, ErrorBanner, Expandable, Loading, Time, useToast } from '../components/ui';
import { useApi } from '../hooks';
import { hasSurface, useSession } from '../session';

// Settings, Connections: the systems this workspace reads, and the routines that
// read them (docs/design/connections.md). It shows what the workspace has; the
// catalog appears when someone asks to add one. Patterns: data-display/table,
// forms/form, content-management/modal to revoke.

interface KindField { name: string; label: string; secret: boolean; required: boolean; hint: string | null; type: 'text' | 'password' | 'textarea' | 'number' | 'select'; options: Array<{ value: string; label: string }> | null }
interface Kind { kind: string; title: string; description: string; group: 'app' | 'generic' | 'existing'; auth: 'key' | 'oauth' | 'existing'; href: string | null; reads: boolean; fields: KindField[]; feeds: Array<{ name: string; title: string; description: string }> }
interface Connection { connector: string; name: string; title: string; status: string; health: string; health_reason: string | null; health_changed_at: string | null; last_signed_in_at: string | null; created_at: string; details: Array<{ label: string; value: string }> }
interface Feed { id: string; name: string; title: string; version: number; status: string; connector: string; connection_name: string | null; target: string; schedule: string | null; health: string; health_reason: string | null; health_changed_at: string | null; next_run_at: string | null; last_run_at: string | null; last_rows_at: string | null; stop_asking: boolean }
interface FeedRun { id: string; trigger: string; status: string; rows_read: number; rows_new: number; rows_amended: number; rows_refused: number; error: string | null; started_at: string; finished_at: string | null; files: Array<{ name: string; bytes: number }> }

const CONNECTION_HEALTH: Record<string, { label: string; tone: string }> = {
  connected: { label: 'Connected', tone: 'ok' }, needs_sign_in: { label: 'Needs sign-in', tone: 'danger' }, failing: { label: 'Failing', tone: 'danger' }, unknown: { label: 'Not checked', tone: '' },
};
const FEED_HEALTH: Record<string, { label: string; tone: string }> = {
  ok: { label: 'Healthy', tone: 'ok' }, unknown: { label: 'Not run yet', tone: '' }, late: { label: 'Late', tone: 'warn' }, empty: { label: 'Gone quiet', tone: 'warn' },
  failing: { label: 'Failing', tone: 'danger' }, drifted: { label: 'Format changed', tone: 'danger' }, held: { label: 'Waiting on its connection', tone: 'warn' },
};
const RUN_STATUS: Record<string, string> = { running: 'Running', waiting: 'Waiting for a sign-off', ok: 'Landed', empty: 'Nothing new', failed: 'Failed', drifted: 'Format changed', declined: 'Declined' };
const TARGET: Record<string, string> = { parties: 'Contacts', schema: 'Records', files: 'Files' };

export function ConnectionsAndFeeds() {
  const { me } = useSession();
  const canAdd = me.role === 'owner' || me.role === 'admin';
  const catalog = useApi<Kind[]>('/v1/connections/catalog');
  const connections = useApi<Connection[]>('/v1/connections');
  const feeds = useApi<Feed[]>('/v1/feeds');
  const toast = useToast();
  const [params] = useSearchParams();
  const [adding, setAdding] = useState<Kind | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [revoking, setRevoking] = useState<Connection | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const failed = params.get('connect_error');
  const reload = () => { void connections.reload(); void feeds.reload(); };

  async function check(c: Connection) {
    setBusy(true); setError(null);
    try {
      const out = await api<{ health: string; detail: string }>(`/v1/connections/${c.connector}/${c.name}/check`, { method: 'POST', body: {} });
      toast(`${c.name}: ${out.detail}`);
      reload();
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  async function revoke() {
    if (!revoking) return;
    setBusy(true); setError(null);
    try {
      await api(`/v1/connectors/${revoking.connector}/${revoking.name}/revoke`, { method: 'POST', body: {} });
      toast(`${revoking.name} revoked`);
      setRevoking(null);
      reload();
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  async function turnOn(c: Connection, preset: { name: string; title: string }) {
    setBusy(true); setError(null);
    try {
      const out = await api<{ state: string }>(`/v1/connections/${c.connector}/${c.name}/feeds`, { body: { preset: preset.name } });
      toast(out.state === 'pending_approval' ? `${preset.title} is waiting for a sign-off in the Inbox` : `${preset.title} is on`);
      reload();
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  const presetsFor = (c: Connection) => (catalog.data?.find((k) => k.kind === c.connector)?.feeds ?? []).filter((p) => !(feeds.data ?? []).some((f) => f.connection_name === c.name && f.title === p.title));

  return (
    <section className={`${cardClass} card-pad stack`} style={{ gap: 12 }} aria-labelledby="connections-h">
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 id="connections-h" style={{ margin: 0 }}>Other systems</h2>
          <p className="small muted" style={{ margin: 0 }}>What Mazu reads from your other systems. A feed is a routine you sign once: it runs by itself, with no agent doing the work, and you hear if it stops working.</p>
        </div>
        {canAdd && !choosing && !adding && <Button variant="secondary" onClick={() => setChoosing(true)}>Add a connection</Button>}
      </div>
      {failed && <Alert tone="danger" role="alert">Google did not connect: {failed === 'access_denied' ? 'you declined it.' : 'try again, and sign in with an account that can see the folder.'}</Alert>}
      <ErrorBanner error={connections.error ?? feeds.error ?? catalog.error ?? error} />

      {choosing && catalog.data && (
        <KindPicker kinds={catalog.data} onCancel={() => setChoosing(false)} onPick={(k) => { setChoosing(false); setAdding(k); }} />
      )}
      {adding && <AddConnection kind={adding} onDone={(added) => { setAdding(null); if (added) reload(); }} />}

      {!connections.data || !feeds.data ? <Loading label="Loading connections" /> : <>
        {(connections.data.length > 0) && (
          <Table label="Connections">
            <caption className="sr-only">Connections</caption>
            <thead><tr><th scope="col">Name</th><th scope="col">Kind</th><th scope="col">Sign-in</th><th scope="col">Details</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{connections.data.map((c) => {
              const shown = c.status !== 'active' ? { label: 'Revoked', tone: '' } : CONNECTION_HEALTH[c.health] ?? CONNECTION_HEALTH.unknown!;
              return (
                <tr key={`${c.connector}/${c.name}`}>
                  <td>{c.name}</td>
                  <td>{c.title}</td>
                  <td>
                    <Badge tone={badgeTone(shown.tone)}>{shown.label}</Badge>
                    {c.status === 'active' && c.health !== 'connected' && c.health_reason && <div className="small faint">{c.health_reason}</div>}
                    {c.status === 'active' && c.last_signed_in_at && <div className="small faint">Last worked <Time iso={c.last_signed_in_at} />.</div>}
                  </td>
                  <td className="small">{c.details.map((d) => <div key={d.label}>{d.label}: {d.value}</div>)}</td>
                  <td>{c.status === 'active' && canAdd && <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {presetsFor(c).map((p) => <Button key={p.name} variant="secondary" size="sm" disabled={busy} onClick={() => void turnOn(c, p)}>Turn on {p.title}</Button>)}
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => void check(c)}>Check now<span className="sr-only"> {c.name}</span></Button>
                    <Button variant="ghost" size="sm" onClick={() => setRevoking(c)}>Revoke {c.name}</Button>
                  </div>}</td>
                </tr>
              );
            })}</tbody>
          </Table>
        )}
        {feeds.data.length > 0 && <FeedsTable feeds={feeds.data} canAct={canAdd} onChanged={() => void feeds.reload()} />}
        {canAdd && hasSurface(me, 'connections') && <AskAgent />}
        {canAdd && !hasSurface(me, 'connections') && connections.data.some((c) => c.status === 'active') && (
          <p className="small muted" style={{ margin: 0 }}>To set up a routine on a connection from a sentence, hire the connections agent from the <Link to="/library">Library</Link>.</p>
        )}
      </>}
      <ConfirmDialog open={!!revoking} title={`Revoke ${revoking?.name ?? ''}?`} confirmLabel="Revoke connection" tone="danger" busy={busy} onConfirm={revoke} onCancel={() => setRevoking(null)}>
        <p style={{ margin: 0 }}>Feeds on it wait until it is added again. The record stays; nothing is deleted.</p>
      </ConfirmDialog>
    </section>
  );
}

// The catalog, from the most structured way in to the least. Pattern: navigation/menu of choices as cards.
function KindPicker({ kinds, onPick, onCancel }: { kinds: Kind[]; onPick: (k: Kind) => void; onCancel: () => void }) {
  const groups: Array<[Kind['group'], string, string]> = [
    ['app', 'Systems Mazu knows', 'Sign in, and turn on a ready-made feed.'],
    ['generic', 'Any system of this shape', 'Add it, then ask the connections agent to set up a feed on it.'],
    ['existing', 'Set up elsewhere', 'These have their own place in Settings.'],
  ];
  return (
    <div className="stack" style={{ gap: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      {groups.map(([group, title, lead]) => {
        const here = kinds.filter((k) => k.group === group);
        if (!here.length) return null;
        return (
          <div key={group} className="stack" style={{ gap: 6 }}>
            <h3 style={{ margin: 0 }}>{title}</h3>
            <p className="small muted" style={{ margin: 0 }}>{lead}</p>
            <ul className="stack" style={{ gap: 6, listStyle: 'none', padding: 0, margin: 0 }}>
              {here.map((k) => (
                <li key={k.kind} className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 16rem' }}><strong>{k.title}</strong><div className="small muted">{k.description}</div></div>
                  {k.href
                    ? (k.href.startsWith('#') ? <a className="small" href={k.href}>Go there</a> : <Link className="small" to={k.href}>Go there</Link>)
                    : <Button variant="secondary" size="sm" onClick={() => onPick(k)}>Add<span className="sr-only"> {k.title}</span></Button>}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <div><Button variant="ghost" onClick={onCancel}>Cancel</Button></div>
    </div>
  );
}

function AddConnection({ kind, onDone }: { kind: Kind; onDone: (added: boolean) => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const set = (k: string, v: string) => setFields({ ...fields, [k]: v });
  async function add(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      if (kind.auth === 'oauth') {
        const { url } = await api<{ url: string }>('/v1/connections/google-drive/connect', { body: { name: name.trim(), return_to: '/admin/connections' } });
        window.location.assign(url);
        return;
      }
      const out = await api<{ detail: string }>('/v1/connections', { body: { kind: kind.kind, name: name.trim(), fields } });
      toast(`${name.trim()} added: ${out.detail}`);
      onDone(true);
    } catch (err) { setError(err); } finally { setBusy(false); }
  }
  return (
    <form className="form-grid" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }} onSubmit={add} aria-label={`Add ${kind.title}`}>
      <h3 className="full" style={{ margin: 0 }}>Add {kind.title}</h3>
      <p className="full small muted" style={{ margin: 0 }}>{kind.description} Mazu looks once, read only, before keeping anything. What you mark secret is sealed: nobody and no agent can read it again.</p>
      <div className="full"><ErrorBanner error={error} focusOnShow /></div>
      <Field label="Name" hint="Lowercase letters, digits, dashes. How agents and feeds name it." required><input className={fieldClass} autoComplete="off" pattern="[a-z0-9_-]{1,60}" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      {kind.fields.map((f) => (
        <Field key={f.name} label={f.label} hint={f.hint ?? undefined} required={f.required} className={f.type === 'textarea' ? 'full' : undefined}>
          {f.type === 'select'
            ? <select className={selectClass} value={fields[f.name] ?? ''} onChange={(e) => set(f.name, e.target.value)}><option value="" disabled>Choose</option>{f.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
            : f.type === 'textarea'
              ? <textarea className={textareaClass} rows={5} autoComplete="off" spellCheck={false} value={fields[f.name] ?? ''} onChange={(e) => set(f.name, e.target.value)} />
              : <input className={fieldClass} type={f.type === 'password' ? 'password' : f.type === 'number' ? 'number' : 'text'} autoComplete={f.secret ? 'new-password' : 'off'} value={fields[f.name] ?? ''} onChange={(e) => set(f.name, e.target.value)} />}
        </Field>
      ))}
      <div className="full row" style={{ gap: 8 }}>
        <Button type="submit" disabled={busy || !name.trim()}>{kind.auth === 'oauth' ? 'Sign in to Google' : busy ? 'Checking…' : `Add ${kind.title}`}</Button>
        <Button type="button" variant="ghost" onClick={() => onDone(false)}>Cancel</Button>
      </div>
    </form>
  );
}

function FeedsTable({ feeds, canAct, onChanged }: { feeds: Feed[]; canAct: boolean; onChanged: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function act(fn: () => Promise<string>) {
    setBusy(true); setError(null);
    try { toast(await fn()); onChanged(); } catch (e) { setError(e); } finally { setBusy(false); }
  }
  const setStatus = (f: Feed, action: 'pause' | 'resume') => act(async () => {
    const out = await propose('config.feed', { action, feed_id: f.id, reason: `${action === 'pause' ? 'Paused' : 'Resumed'} in Settings` });
    return out.state === 'applied' ? `${f.title} ${action === 'pause' ? 'paused' : 'resumed'}` : `${f.title}: waiting for a sign-off in the Inbox`;
  });
  // A person's standing decision, for this feed's imports alone. Anything unusual still asks: it covers these reasons and no others.
  const stopAsking = (f: Feed) => act(async () => {
    await propose('config.standing_decision', { action: 'set', decision: 'approve', schema_id: 'import.batch', proposer: `system:feed:${f.id}`, reasons: ['signoff_required'], label: `Runs of ${f.title}` });
    return `${f.title} lands without asking, unless a run looks unusual`;
  });
  return (
    <div className="stack" style={{ gap: 8 }}>
      <h3 style={{ margin: 0 }}>Feeds</h3>
      <ErrorBanner error={error} />
      <Table label="Feeds">
        <caption className="sr-only">Feeds</caption>
        <thead><tr><th scope="col">Feed</th><th scope="col">Reads</th><th scope="col">Runs</th><th scope="col">Health</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
        <tbody>{feeds.map((f) => {
          const shown = f.status === 'paused' ? { label: 'Paused', tone: '' } : FEED_HEALTH[f.health] ?? FEED_HEALTH.unknown!;
          return (
            <tr key={f.id}>
              <td>{f.title}<div className="small faint">Brings in: {TARGET[f.target] ?? f.target}</div></td>
              <td className="small">{f.connection_name && f.connector !== 'webhook' ? f.connection_name : f.connector === 'webhook' ? 'What another system pushes' : 'Your mailboxes'}</td>
              <td className="small">{f.schedule ?? 'As things arrive'}{f.last_run_at && <div className="faint">Last ran <Time iso={f.last_run_at} /></div>}</td>
              <td>
                <Badge tone={badgeTone(shown.tone)}>{shown.label}</Badge>
                {f.status === 'active' && f.health !== 'ok' && f.health_reason && <div className="small faint">{f.health_reason}</div>}
                <FeedHistory id={f.id} title={f.title} />
              </td>
              <td>{canAct && <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {f.status === 'active' && f.schedule && <Button variant="ghost" size="sm" disabled={busy} onClick={() => void act(async () => { await api(`/v1/feeds/${f.id}/run`, { method: 'POST', body: {} }); return `${f.title} runs within the minute`; })}>Run now<span className="sr-only"> {f.title}</span></Button>}
                {f.status === 'active' && f.target === 'parties' && !f.stop_asking && <Button variant="ghost" size="sm" disabled={busy} onClick={() => void stopAsking(f)}>Stop asking<span className="sr-only"> about {f.title}</span></Button>}
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => void setStatus(f, f.status === 'paused' ? 'resume' : 'pause')}>{f.status === 'paused' ? 'Resume' : 'Pause'}<span className="sr-only"> {f.title}</span></Button>
              </div>}</td>
            </tr>
          );
        })}</tbody>
      </Table>
    </div>
  );
}

function FeedHistory({ id, title }: { id: string; title: string }) {
  return <Expandable label={[`Runs of ${title}`, `Hide runs of ${title}`]}><Runs id={id} /></Expandable>;
}

function Runs({ id }: { id: string }) {
  const d = useApi<{ runs: FeedRun[] }>(`/v1/feeds/${id}`);
  if (!d.data) return <Loading label="Loading runs" />;
  if (!d.data.runs.length) return <p className="small faint" style={{ margin: 0 }}>It has not run yet.</p>;
  return (
    <ul className="small stack" style={{ gap: 4, listStyle: 'none', padding: 0, margin: '4px 0 0' }}>
      {d.data.runs.slice(0, 10).map((r) => (
        <li key={r.id}>
          <Time iso={r.started_at} />: {RUN_STATUS[r.status] ?? r.status}
          {r.rows_read > 0 && <> ({r.rows_read} read, {r.rows_new} new, {r.rows_amended} changed{r.rows_refused ? `, ${r.rows_refused} left for you` : ''})</>}
          {r.error && <div className="faint">{r.error}</div>}
        </li>
      ))}
    </ul>
  );
}

// Asks the connections agent, the way the agent builder is asked. Pattern: forms/textarea.
function AskAgent() {
  const toast = useToast();
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function ask(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const out = await api<{ outcome: string }>('/v1/connections/requests', { body: { message: message.trim() } });
      toast(out.outcome === 'unmatched' ? 'The connections agent is not running. Turn it on from its page.' : 'Sent. Its draft comes to your Inbox to sign.');
      if (out.outcome !== 'unmatched') setMessage('');
    } catch (err) { setError(err); } finally { setBusy(false); }
  }
  return (
    <form className="stack" style={{ gap: 6, borderTop: '1px solid var(--border)', paddingTop: 12 }} onSubmit={ask} aria-label="Ask the connections agent">
      <ErrorBanner error={error} />
      <Field label="Ask the connections agent" hint="Say what to bring in, from where, and how often. It looks, drafts a feed, and you sign it. For example: every morning, take the new sales CSV from our partner's file server into contacts.">
        <textarea className={textareaClass} rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
      </Field>
      <div><Button type="submit" variant="secondary" disabled={busy || message.trim().length < 10}>Ask</Button></div>
    </form>
  );
}
